import { Link, Outlet, useLocation, useParams } from "react-router-dom";
import { Printer, ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function AppShell({ companies }) {
  const location = useLocation();
  const params = useParams();
  const onWorkspace = location.pathname.startsWith("/c/");
  const currentCompany = companies.find((c) => c.id === params.companyId);
  const { authRequired, signOut, username } = useAuth();

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onWorkspace && (
              <Link
                to="/"
                className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-neutral-200 hover:bg-neutral-100 transition-colors"
                aria-label="Back to companies"
                data-testid="back-to-companies-button"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
            )}
            <Link to="/" className="flex items-center gap-2 group">
              <div className="h-9 w-9 rounded-md bg-neutral-900 flex items-center justify-center text-white shadow-sm">
                <Printer className="h-4 w-4" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-semibold tracking-tight text-neutral-900">A6 Sticker Studio</div>
                <div className="text-[11px] text-neutral-500 -mt-0.5">Stock label generator</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3 text-sm text-neutral-600">
            {onWorkspace && currentCompany && (
              <div className="hidden sm:flex items-center gap-2">
                {currentCompany.logo_data_url ? (
                  <img
                    src={currentCompany.logo_data_url}
                    alt={currentCompany.name}
                    className="h-7 w-7 object-contain rounded border border-neutral-200 bg-white"
                  />
                ) : (
                  <div className="h-7 w-7 rounded bg-neutral-100 border border-neutral-200 text-[10px] flex items-center justify-center font-semibold text-neutral-500">
                    {currentCompany.name.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <span className="font-medium text-neutral-900">{currentCompany.name}</span>
              </div>
            )}
            {authRequired && (
              <button
                type="button"
                onClick={signOut}
                title={username ? `Signed in as ${username}` : "Sign out"}
                data-testid="logout-button"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-neutral-200 text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 transition-colors text-sm"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Sign out</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 flex items-center justify-between text-xs text-neutral-500">
          <div>A6 Landscape · 148 × 105 mm · PDF</div>
          <div className="hidden sm:block">Designed for sticker label printing</div>
        </div>
      </footer>
    </div>
  );
}
